import { ChangeDetectorRef, Component, DestroyRef, OnDestroy, OnInit, inject, signal } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { finalize, from, tap } from 'rxjs';

import { MapService } from './services/map.service';
import * as L from 'leaflet';
import 'leaflet.markercluster';
import { ICountry } from '../../shared/interfaces/ICountry';
import { IGeolocation } from '../../shared/interfaces/IGeolocation';
import type { ILocation } from '../../shared/interfaces/ILocation';
import type { PersonDetail } from '../../shared/interfaces/person-detail.interface';
import { CountryService } from '../../shared/services/country.service';
import { LocationService } from './services/location.service';
import { DesignService } from '../../shared/services/design.service';
import { NgClass } from '@angular/common';
import { DrawerComponent } from './components/drawer/drawer.component';
import { LoadingIndicatorComponent } from '../../shared/components/loading-indicator/loading-indicator.component';
import { LeafletDirective } from '@bluehalo/ngx-leaflet';
import { LeafletMarkerClusterDirective } from '@bluehalo/ngx-leaflet-markercluster';
import { SiCardComponent } from '@siemens/element-ng/card';
import {
    buildCoordinateCount,
    findLocationByMarkerCoordinates,
    getFinalMarkerCoordinates,
    MAP_OPTIONS,
    MARKER_CLUSTER_OPTIONS,
    MarkerClusterOptions
} from './map.operations';

const DEFAULT_SELECTED_ZOOM = 17;

@Component({
    selector: 'app-map',
    templateUrl: './map.component.html',
    styleUrls: ['./map.component.scss'],
    imports: [
        NgClass,
        SiCardComponent,
        DrawerComponent,
        LoadingIndicatorComponent,
        LeafletDirective,
        LeafletMarkerClusterDirective
    ]
})
export class MapComponent implements OnInit, OnDestroy {
    private readonly markerIconSize: L.PointExpression = [35, 35];
    private readonly markerIconAnchor: L.PointExpression = [18, 32];

    private activatedRoute = inject(ActivatedRoute);
    private countryService = inject(CountryService);
    private mapService = inject(MapService);
    private locationService = inject(LocationService);
    private designService = inject(DesignService);
    private cdr = inject(ChangeDetectorRef);
    private destroyRef = inject(DestroyRef);

    private map: L.Map;
    private readonly markerSalId = new Map<L.Marker, string>();
    private readonly selectedMarker = signal<L.Marker | null>(null);
    // Kept separately from selectedMarker so the active icon can be reapplied to a fresh marker
    // instance after the map remounts (e.g. when the drawer layout switch recreates the Leaflet map).
    private selectedSalId: string | null = null;
    private latestLocationRequestId = 0;
    // Target zoom after a pin click; survives the map remount caused by the drawer layout switch.
    private preservedZoom: number | null = null;

    protected readonly isDrawerOpen = signal<boolean>(!!this.activatedRoute.snapshot.data.view);
    protected readonly view = signal<'location' | 'country'>('location');
    protected readonly focusedLocation = signal<ILocation | null>(null);
    protected readonly focusedCountry = signal<ICountry | null>(null);
    protected readonly allLocations = signal<IGeolocation[]>([]);
    protected readonly markers = signal<L.Marker[]>([]);
    protected readonly isLoading = signal<boolean>(false);
    // loadingScreen;
    protected readonly userDetail = signal<PersonDetail | null>(null);
    protected readonly mapOptions: L.MapOptions = MAP_OPTIONS;
    protected readonly markerClusterOptions: MarkerClusterOptions = MARKER_CLUSTER_OPTIONS;

    public ngOnInit(): void {
        this.initializeViewportScroll();
        this.loadLocations();
    }

    private initializeViewportScroll(): void {
        setTimeout(() => {
            window.scrollTo(0, 31);
        }, 0);
    }

    private loadLocations(): void {
        from(this.mapService.getGeolocations())
            .pipe(
                tap(locations => {
                    this.allLocations.set(locations);
                    this.initMap();
                    this.subscribeToRouteParams();
                }),
                takeUntilDestroyed(this.destroyRef)
            )
            .subscribe();
    }

    private subscribeToRouteParams(): void {
        this.activatedRoute.params
            .pipe(
                tap(params => this.handleRouteParams(params)),
                takeUntilDestroyed(this.destroyRef)
            )
            .subscribe();
    }

    private handleRouteParams(params: { cn?: string; id?: string }): void {
        const view = this.activatedRoute.snapshot.data['view'] as 'location' | 'country' | undefined;

        if (!params || !view) {
            return;
        }

        this.prepareDrawerView(view);

        if (view === 'location') {
            this.handleLocationRoute(params);
            return;
        }

        if (params.id) {
            this.getCountryInfo(params.id, '');
        }
    }

    private prepareDrawerView(view: 'location' | 'country'): void {
        document.body.style.overflow = 'hidden';
        if (!this.isDrawerOpen()) {
            this.toggleInfoDrawer();
        }
        this.view.set(view);
    }

    private handleLocationRoute(params: { cn?: string; id?: string }): void {
        if (params.cn && params.id) {
            this.loadUserDetails(params.id, params.cn);
        } else {
            this.userDetail.set(null);
        }

        if (params.id) {
            this.selectlocationMarker(params.id);
        }
    }

    private loadUserDetails(salId: string, encodedCn: string): void {
        const cn = decodeURIComponent(encodedCn);

        this.locationService
            .getPersonLocationAddresses(salId, cn)
            .pipe(
                tap(response => {
                    if (!response) {
                        return;
                    }

                    this.userDetail.set({
                        cn,
                        gid: response.gid,
                        name: response.name,
                        nameNational: response.nameNational,
                        orgCode: response.orgCode,
                        building: response.building,
                        room: response.room,
                        location: response.location?.locCode
                    });
                    this.cdr.detectChanges();
                }),
                takeUntilDestroyed(this.destroyRef)
            )
            .subscribe();
    }

    private selectlocationMarker(SALID: string): void {
        this.setLoadingState(true);
        this.selectedSalId = SALID;
        // Look up the marker by SALID instead of coordinates: duplicate coordinates get a random
        // jitter applied in getFinalMarkerCoordinates, so exact lat/lng matching can miss the marker.
        const newMarker = this.markers().find(marker => this.markerSalId.get(marker) === SALID);
        if (newMarker) {
            this.markerWasClicked(newMarker, SALID);
        } else {
            this.view.set('location');
            this.openDrawer();
            this.getLocationInfo(SALID);
        }
    }

    private getLocationInfo(SALID: string): void {
        const requestId = ++this.latestLocationRequestId;
        from(this.locationService.getLocationData(SALID))
            .pipe(
                tap(location => {
                    if (!location || requestId !== this.latestLocationRequestId) {
                        return;
                    }
                    this.focusedLocation.set(location);
                    this.focusedCountry.set(location.country);
                    this.cdr.detectChanges();
                    this.recenterMapAfterLayoutChange();
                }),
                finalize(() => {
                    if (requestId === this.latestLocationRequestId) {
                        this.setLoadingState(false);
                    }
                }),
                takeUntilDestroyed(this.destroyRef)
            )
            .subscribe();
    }

    private getCountryInfo(countryCode: string, state: string, requestId?: number): void {
        this.countryService
            .getCountryDetailData(countryCode, state)
            .pipe(
                tap(country => {
                    if (requestId !== undefined && requestId !== this.latestLocationRequestId) {
                        return;
                    }
                    this.focusedCountry.set(country);
                    this.cdr.detectChanges();
                }),
                takeUntilDestroyed(this.destroyRef)
            )
            .subscribe();
    }

    private toggleInfoDrawer(): void {
        this.isDrawerOpen.update(value => !value);
    }

    public ngOnDestroy(): void {
        document.body.style.overflow = 'auto';
        window.scrollTo(0, 0);
    }

    public onMapReady(map: L.Map): void {
        this.map = map;
        this.map.attributionControl.setPrefix(false);
        this.initMap();

        // The map remounts when the drawer appears (see template @if/@else), so only recenter on the
        // user's browser location when we are not already focusing a location/country from the route.
        if (!this.focusedLocation() && !this.focusedCountry()) {
            this.centerOnUserLocation();
        }

        // Ensure Leaflet calculates map/marker layout immediately after initial render.
        requestAnimationFrame(() => {
            this.map.invalidateSize();
        });
    }

    private centerOnUserLocation(): void {
        if (!navigator.geolocation) {
            return;
        }
        navigator.geolocation.getCurrentPosition(
            position => {
                // Guard against the async geolocation result resolving after a location/country was focused,
                // which would otherwise snap the map away from the focused pin.
                if (this.focusedLocation() || this.focusedCountry()) {
                    return;
                }
                this.map.setView(L.latLng(position.coords.latitude, position.coords.longitude), 5);
                this.cdr.detectChanges();
            },
            () => {
                // Permission denied or unavailable — keep default center
            }
        );
    }

    private markerWasClicked(newMarker: L.Marker, salId?: string): void {
        this.applyActiveMarkerIcon(newMarker);

        this.view.set('location');
        this.openDrawer();
        this.cdr.detectChanges();

        const selectedLocation = findLocationByMarkerCoordinates(this.allLocations(), newMarker);
        const resolvedSalId = salId ?? this.markerSalId.get(newMarker) ?? selectedLocation?.salId?.toString();
        this.selectedSalId = resolvedSalId ?? null;
        if (!resolvedSalId) {
            this.setLoadingState(false);
            return;
        }

        this.getLocationInfo(resolvedSalId);
        this.recenterMapAfterLayoutChange();
    }

    private applyActiveMarkerIcon(marker: L.Marker): void {
        const currentSelectedMarker = this.selectedMarker();
        if (currentSelectedMarker && currentSelectedMarker !== marker) {
            currentSelectedMarker.setIcon(this.createMarkerIcon('/assets/pins/pin_siemens_default.svg'));
        }
        this.selectedMarker.set(marker);
        const selectedIcon = this.designService.designSet().pinUrl;
        marker.setIcon(this.createMarkerIcon(selectedIcon));
        marker.setZIndexOffset(999);
    }

    // Re-highlights the previously selected marker after initMap() rebuilds the marker instances,
    // which happens when the map remounts (e.g. the drawer @if/@else layout switch in the template).
    private reapplySelectedMarkerIcon(markerList: L.Marker[]): void {
        if (!this.selectedSalId) {
            return;
        }

        const marker = markerList.find(candidate => this.markerSalId.get(candidate) === this.selectedSalId);
        if (marker) {
            this.applyActiveMarkerIcon(marker);
        }
    }

    private recenterMapAfterLayoutChange(): void {
        const marker = this.selectedMarker();
        if (!this.map || !marker) {
            return;
        }

        const markerLatLng = marker.getLatLng();
        const zoom = this.preservedZoom ?? DEFAULT_SELECTED_ZOOM;
        requestAnimationFrame(() => {
            this.map.invalidateSize();
            this.map.setView(markerLatLng, zoom, { animate: false });

            // A second pass helps when the drawer appears for the first time and changes layout width.
            setTimeout(() => {
                this.map.invalidateSize();
                this.map.setView(markerLatLng, zoom, { animate: false });
            }, 200);
        });
    }

    private openDrawer(): void {
        if (!this.isDrawerOpen()) {
            this.toggleInfoDrawer();
            this.cdr.detectChanges();
        }
    }

    private createMarkerIcon(iconUrl: string): L.Icon {
        return new L.Icon({
            iconUrl,
            iconSize: this.markerIconSize,
            iconAnchor: this.markerIconAnchor
        });
    }

    private initMap(): void {
        if (!this.map || this.allLocations().length === 0) {
            return;
        }

        this.markerSalId.clear();

        const coordinateCount = buildCoordinateCount(this.allLocations());
        const nextMarkers = this.allLocations()
            .map((location, _i) => {
                const finalCoordinates = getFinalMarkerCoordinates(location, coordinateCount);
                if (!finalCoordinates) {
                    return null;
                }

                const marker = L.marker([finalCoordinates.lat, finalCoordinates.lng], {
                    icon: this.createMarkerIcon('/assets/pins/pin_siemens_default.svg')
                });
                this.markerSalId.set(marker, location.salId.toString());
                marker.on('click', () => {
                    this.preservedZoom = Math.min(
                        Math.max(this.map.getZoom() + 1, DEFAULT_SELECTED_ZOOM),
                        this.map.getMaxZoom()
                    );
                    this.setLoadingState(true);
                    this.markerWasClicked(marker);
                });

                return marker;
            })
            .filter((marker): marker is L.Marker => marker !== null);

        // Replace by a new array reference so ngx-leaflet-markercluster updates immediately.
        this.markers.set([...nextMarkers]);
        this.reapplySelectedMarkerIcon(nextMarkers);
        this.cdr.detectChanges();
    }

    private setLoadingState(value: boolean): void {
        this.isLoading.set(value);
        this.cdr.detectChanges();
    }
}
